import type { ValidationError } from '../api/types';

/**
 * Extract meaningful error message from API error response
 */
export const extractErrorMessage = (error: any): string => {
  console.log('🔍 Extracting error message from:', {
    hasResponse: !!error.response,
    status: error.response?.status,
    data: error.response?.data,
    message: error.message,
    code: error.code,
  });

  if (error.response?.data?.detail) {
    const detail = error.response.data.detail;
    
    // Handle 422 validation errors (array format)
    if (Array.isArray(detail)) {
      return detail
        .map((err: ValidationError) => {
          // Create user-friendly error messages
          const field = err.loc[err.loc.length - 1];
          
          // Customize common validation messages
          if (err.type === 'value_error.email') {
            return 'Please enter a valid email address';
          }
          if (err.type === 'string_too_short') {
            return `${String(field).charAt(0).toUpperCase() + String(field).slice(1)} must be at least ${err.ctx?.min_length} characters`;
          }
          if (err.type === 'missing') {
            return `${String(field).charAt(0).toUpperCase() + String(field).slice(1)} is required`;
          }
          
          // Default to the API message
          return err.msg;
        })
        .join(', ');
    }
    
    // Handle simple string errors
    if (typeof detail === 'string') {
      return detail;
    }
  }
  
  // Handle network errors
  if (error.code === 'ERR_NETWORK' || error.code === 'ERR_UNAVAILABLE') {
    return 'Unable to connect to the server. Please check your internet connection and try again.';
  }
  
  if (error.code === 'ECONNABORTED') {
    return 'Request timed out. Please try again.';
  }
  
  // Fallback to general error message
  return error.message || 'An unexpected error occurred';
};

/**
 * Extract validation errors in array format for display components
 */
export const extractValidationErrors = (error: any): ValidationError[] | null => {
  if (error.response?.data?.detail && Array.isArray(error.response.data.detail)) {
    return error.response.data.detail;
  }
  return null;
};

/**
 * Check if error is a specific type
 */
export const isValidationError = (error: any): boolean => {
  return error.response?.status === 422 && Array.isArray(error.response?.data?.detail);
};

export const isUnauthorizedError = (error: any): boolean => {
  return error.response?.status === 401;
};

export const isForbiddenError = (error: any): boolean => {
  return error.response?.status === 403;
};

export const isNotFoundError = (error: any): boolean => {
  return error.response?.status === 404;
};

/**
 * Map common API error messages to user-friendly versions
 */
export const getFriendlyErrorMessage = (message: string): string => {
  const errorMap: Record<string, string> = {
    'Email address is already registered': 'This email is already in use. Try logging in instead.',
    'Invalid or expired verification code': 'The code you entered is incorrect or has expired. Please try again.',
    'Incorrect email or password': 'Email or password is incorrect. Please check and try again.',
    'Account is inactive': 'Your account has been deactivated. Please contact support.',
    'Invalid or expired verification token': 'Your verification session has expired. Please request a new code.',
    'Password is incorrect': 'Current password is incorrect. Please try again.',
  };
  
  return errorMap[message] || message;
};

/**
 * Complete error processing for Redux actions
 */
export const processApiError = (error: any) => {
  const validationErrors = extractValidationErrors(error);
  const message = getFriendlyErrorMessage(extractErrorMessage(error));
  
  return {
    message,
    validationErrors,
    isValidation: !!validationErrors,
    statusCode: error.response?.status,
  };
}; 